import './media.scss';

import React, { CSSProperties, ReactNode, useRef, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import Routes, { BackRoutes, LocationDescriptor } from '../../routes';
import { Maybe } from '../../core';
import { JobModel } from '../../entities';
import { MenuItem } from '../input/menuItems';
import { Link } from '../layout';
import { MenuButton } from '../menu';
import { IngestingSVGIcon, TextInputErrorSVGIcon, TextInputWarningSVGIcon } from '../svg';
import { getMetadataInputLabel, JobMetadataField } from '../../entities/job/jobMetadata';

import { JobTitle } from './jobTitle';
import { JobCardPreview } from './jobCardPreview';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { ToggleLayout } from '../input/toggleInput';
import { sanitizeHtml } from '../../pages/searchResultsPage/util';
import { View } from '../../services/ui/utils';
import { TranscriptFormat } from '@clipr/lib';
import { notifyError } from '../../services/notifications';
import { TranscriptFormatItems } from '../playerTranscripts';
import { BrowserIngestionEstimate, IngestionProgress } from './ingestionProgress';

/** The time window in which a Job is considered New and will have a new label. */
const NEW_MARGIN_HOURS = 6;
const DESCRIPTION_PREVIEW_LENGTH = 80;

export function shouldRevealFullDescription(description?: Maybe<string>): boolean {
  return (description?.length ?? 0) > DESCRIPTION_PREVIEW_LENGTH;
}

type Props = {
  model: JobModel;
  route?: LocationDescriptor;
  aspectRatio?: Maybe<number>;
  teamId?: string;
  view?: View;
  target?: string;
};

type JobMenuDropdownOptionsProps = {
  model: JobModel;
  teamId?: string;
};

const CardMetadataSectionFields = [
  'startTime',
  'endTime',
  'track',
  'level',
  'featured',
  'publishDate',
  'location'
] as JobMetadataField[];

export const JobCard = observer(({
  model,
  teamId,
  view,
  target = '_self',
  route = model.isDocumentJob
    ? BackRoutes[Routes.userDocument()].route({ id: model.id, teamId })
    : BackRoutes[Routes.userVideo()].route({ id: model.id, teamId }),
  aspectRatio = 9 / 16
}: Props) => {
  const isNew = model.createdAtDate.diffNow('hours').as('hours') > -NEW_MARGIN_HOURS; // diff will be negative
  const previewStyle: CSSProperties = {
    ['--aspect-ratio' as string]: aspectRatio,
  };

  const { widgetService } = model.store;
  const isWidgetMode = widgetService.isWidgetMode;
  const { metadata } = model;
  const team = (teamId && model.store.teamManager.getTeam(teamId)) || null;
  const allowHover = !model?.store.ui.isTouchBased;

  const menuRef = useRef<HTMLDivElement>(null);

  let linkRoute: LocationDescriptor | string | null = null;
  if (model.isMediaAccessible && model.isPublished) {
    linkRoute = isWidgetMode
      ? Routes.playerWidget(model.id, {
        teamId
      })
      : route;
  }

  const [isHovered, setHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const revealFullDescription =
    isHovered && shouldRevealFullDescription(model.cardDescription);

  const metadataFields = view === View.Carousel
    ? []
    : getVisibleMetadataFields(model, team);

  const className = classNames('job-card-new', {
    'carousel-card': view === View.Carousel,
    'transient': model.isTransient,
    'status-not-ingested': !model.status,
    'status-done': model.isDone && model.isPublished && model.isMediaDone,
    'status-is-ingesting': model.isInProgress || model.isMediaProcessing,
    'status-is-analyzing': model.isProcessing,
    'status-is-failed': model.isFailed || model.isMediaFailed,
    'status-is-unpublish': !model.isPublished,
    'description-revealed': revealFullDescription,
    'has-metadata': metadataFields.length > 0,
    hovered: isHovered
  });

  const handleMouseEnter = () => {
    if (!allowHover) return;
    const el = cardRef.current;
    const parent = el?.parentElement;
    if (el && parent)
      parent.style.minHeight = `${el.offsetHeight}px`;
    setHovered(true);
  }

  const handleMouseLeave = () => {
    setHovered(false);
    const parent = cardRef.current?.parentElement;
    if (parent) parent.style.minHeight = '';
  }

  return (
    <div
      ref={cardRef}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-job-id={model.id}
      data-job-status={model.testStatus}>

      <Link
        target={target}
        to={linkRoute}
        className="card-preview aspect-ratio-box"
        style={previewStyle}>
        <JobCardPreview
          teamId={teamId}
          model={model}
          isNew={isNew} />
      </Link>

      <Link
        target={target}
        to={linkRoute}
        className="card-title">
        {model.highlights &&
          <div className="snippet">
            {sanitizeHtml(model.highlights[0].snippets[0])}
          </div>
        }
        {!model.highlights &&
          <JobTitle
            isAudioSource={model.isAudioSource}
            title={model.title} />
        }
      </Link>

      {view !== View.Carousel && model.cardDescription &&
        <Link
          target={target}
          to={linkRoute}
          className={classNames('card-description', {
            'description-revealed': revealFullDescription,
          })}>
          {model.cardDescription}
        </Link>}

      {metadata && metadataFields.length > 0 && (
        <Link to={null} className="card-metadata">
          {metadataFields.map((field) => (
            <div className="metadata-block" key={field}>
              <h6>{team?.getMetadataFieldAlias(field) ?? getMetadataInputLabel(field)}</h6>
              <p>{metadata[field]}</p>
            </div>
          ))}
        </Link>
      )}

      {view !== View.Carousel &&
        <div className="card-teams">
          {model.foundInTeams?.slice(0, 1).map((teamStub) => (
            <RouterLink
              key={teamStub.id}
              to={Routes.teamDashboard(teamStub.id)}
              className="card-team-name">
              {teamStub.name || teamStub.id}
            </RouterLink>
          ))}
          {model.foundInTeams?.length > 1 && <>
            <span className="card-team-more">+{model.foundInTeams.length - 1}</span>
            <div className="card-teams-tooltip">
              {model.foundInTeams.slice(1).map((teamStub) => (
                <RouterLink
                  key={teamStub.id}
                  to={Routes.teamDashboard(teamStub.id)}
                  className="card-team-name">
                  {teamStub.name || teamStub.id}
                </RouterLink>
              ))}
            </div>
          </>}
        </div>}

      <div className="card-footer">
        {view !== View.Carousel &&
          <Link to={null} className="card-summary">
            {model.createdAtRelativeLabel}
          </Link>}

        <div className="card-menu" ref={menuRef}>
          {!model.isMediaProcessing &&
            model.hasPermission('UserViewJobMenu') && (
              <MenuButton
                layout="horizontal"
                portalToBody
                tooltip="Manage This Upload"
                aria-label="Manage This Upload">
                <JobMenuDropdownOptions
                  model={model}
                  teamId={teamId} />
              </MenuButton>
            )}
        </div>
      </div>
    </div>
  );
});

function getVisibleMetadataFields(
  model: JobModel,
  team: ReturnType<JobModel['store']['teamManager']['getTeam']> | null,
): JobMetadataField[] {
  const { metadata } = model;
  if (!metadata || !model.hasMetadata)
    return [];

  const baseFields = [...CardMetadataSectionFields];
  const showSession =
    (team && team.visibleMetadataFields?.includes('session')) ||
    (!team && metadata.session);
  if (showSession && !baseFields.includes('session'))
    baseFields.push('session');

  return baseFields.filter((field) => {
    if (team && !team.visibleMetadataFields?.includes(field)) return false;
    return Boolean(metadata[field]);
  });
}

export const JobMenuDropdownOptions = observer(({ model, teamId }: JobMenuDropdownOptionsProps) => {
  const isWidgetMode = model.store.widgetService.isWidgetMode;
  const navigate = useNavigate();

  const { pathname, search } = useLocation();
  const trainerPlayerRoute = BackRoutes[Routes.trainerVideo()].route({ id: model.id, teamId, currentPath: search ? pathname + search : pathname });

  const onDownloadTranscript = async (format: string) => {
    const args = {
      format: format as TranscriptFormat
    };

    const [res, err] = await model.apiGetJobTranscript(args);

    if (!res || err) {
      return notifyError(model, 'Transcript download failed.');
    }

    return res;
  }

  if ((
    model.isMediaAccessible ||
    model.isLiveStreamEnded) &&
    model.isPublished) {
    return (
      <>
        {!isWidgetMode &&
          teamId &&
          model?.hasPermission('EditTrainerMoment') &&
          model.isMediaDone && (
            <MenuItem
              isSelected={false}
              model={{ value: 'Open in Trainer Player', label: 'Open in Trainer Player' }}
              onClick={() => {
                const { state, ...to } = trainerPlayerRoute;
                navigate(to, { state });
              }}
            />
          )}

        {model.hasPermission('UserEditJob') && (
          <>
            {model.hasPermission('EditVideoDetails') && (
              <MenuItem
                isSelected={false}
                model={{ value: 'Details', label: model.isDocumentTranslation ? 'Document Details' : 'Video Details' }}
                hoverTooltip="View details for this upload, such as description, tags, language, visibility, and thumbnail."
                onClick={(_evt) => {
                  model.dispatch('openVideoDetailsWindow', {
                    jobId: model.id,
                    teamId: teamId,
                    layoutType: 'full',
                  });
                }} />
            )}

            <MenuItem
              isSelected={false}
              model={{ value: 'Rename', label: 'Rename' }}
              onClick={(_evt) => {
                model.dispatch('openRenameJobWindow', {
                  page: teamId ? 'Libraries' : 'User',
                  jobId: model.id,
                  title: model.title,
                  isDocumentTranslation: model.isDocumentTranslation,
                });
              }} />

            <MenuItem
              isSelected={false}
              model={{ value: 'Update thumbnail', label: 'Update thumbnail' }}
              hoverTooltip="Change this video's preview image."
              onClick={(_evt) => {
                model.dispatch('openUploadThumbnailWindow', {
                  jobId: model.id,
                });
              }} />

            {model.hasPermission('EditIsPublic') && (
              <MenuItem
                model={{ value: 'Visibility', label: 'Visibility' }}
                isSelected={model.isPublic}
                withToggle
                toggleOptions={{
                  toggleLayout: ToggleLayout.Buttons,
                  toggledLabel: 'Shareable',
                  notToggledLabel: 'Private',
                  toggledTooltip: 'Allows this upload to be shared with others who have access.',
                  notToggledTooltip: 'Limits who can access this upload.',
                }}
                toggleModel={model.publicToggleModel}
                disabled={model.isPublishing}
                onClick={(evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  model.handlePublishVideo();
                }} />
            )}

            {((teamId &&
              model.store.teamManager
                .getTeam(teamId)
                ?.hasPermission('CreateJob')) ||
              model.hasPermission('UserCopyJob')) &&
              model.isDone && (
                <MenuItem
                  isSelected={false}
                  model={{ value: 'Make a copy', label: 'Make a copy' }}
                  onClick={(_evt) => {
                    model.dispatch('openCopyJobWindow', {
                      jobId: model.id,
                      teamId: teamId,
                    });
                  }} />
              )}
            {model.store.user?.hasPermission('UserCanAddToTeams') && (
              <MenuItem
                isSelected={false}
                model={{ value: 'Add to library', label: 'Add to library' }}
                onClick={(_evt) => {
                  model.dispatch('openAddJobToTeamWindow', {
                    jobId: model.id,
                  });
                }} />
            )}
          </>
        )}
        {(
          (teamId && model.store.teamManager.getTeam(teamId)?.hasPermission('EditTeam')) ||
          (!teamId && model.hasPermission('UserEditJob'))) &&
          model.isMediaDone && (
            <MenuItem
              isSelected={false}
              model={{ value: 'Download original video', label: 'Download original video' }}
              onClick={() => {
                model.dispatch('openDownloadJobWindow', {
                  jobId: model.id,
                  teamId: teamId
                });
              }}
            />
          )}

        <MenuItem
          isSelected={false}
          model={{ value: 'Download transcript', label: 'Download transcript' }}
          onClick={(_evt) => {
            model.dispatch('openDownloadTranscriptWindow', {
              message: 'Transcript format',
              onSubmit: (format: string) => onDownloadTranscript(format),
              formatItems: TranscriptFormatItems,
              title: 'Download Transcript'
            });
          }} />

        {model.slideDeckUrl && 
          <MenuItem
            isSelected={false}
            model={{ value: 'Download slides', label: 'Download slides' }}
            onClick={(_evt) => {
              model.dispatch('openDownloadJobSlidesWindow', {
              jobId: model.id
              });
            }} />
        }

        {teamId &&
          model.store.teamManager
            .getTeam(teamId)
            ?.hasPermission('RemoveJob') &&
          (!model.ownerTeamId || model.ownerTeamId !== teamId) && (
            <MenuItem
              isSelected={false}
              model={{ value: 'Remove from library', label: 'Remove from library' }}
              onClick={(_evt) => {
                model.dispatch('openRemoveJobFromTeamWindow', {
                  jobId: model.id,
                  teamId: teamId,
                });
              }} />
          )}

        {model.hasPermission('DeleteJob') &&
          (model.source.type === 'ExternalLibrary' ? (
            <MenuItem
              isSelected={false}
              model={{
                value: 'Remove from CLIPr',
                label: 'Remove from CLIPr',
              }}
              onClick={(_evt) => {
                model.dispatch('openRemoveJobFromCliprWindow', {
                  teamId: teamId,
                  jobId: model.id,
                });
              }} />
          ) : (
            <MenuItem
              isSelected={false}
              model={{ value: 'Delete', label: 'Delete' }}
              onClick={(_evt) => {
                model.dispatch('openDeleteJobWindow', {
                  jobId: model.id,
                  teamId: teamId,
                });
              }} />
          ))}
      </>
    );
  } else if (model.isMediaFailed) {

    return model.hasPermission('DeleteJob') ? (
      model.source.type === 'ExternalLibrary' ? (
        <MenuItem
          isSelected={false}
          model={{
            value: 'Remove from CLIPr',
            label: 'Remove from CLIPr',
          }}
          onClick={(_evt) => {
            model.dispatch('openRemoveJobFromCliprWindow', {
              teamId: teamId,
              jobId: model.id,
            });
          }} />
      ) : (
        <MenuItem
          isSelected={false}
          model={{ value: 'Delete', label: 'Delete' }}
          onClick={(_evt) => {
            model.dispatch('openDeleteJobWindow', {
              jobId: model.id,
              teamId: teamId,
            });
          }} />
      )
    ) : null;
  } else if (!model.isPublished) {

    return (
      <>
        {teamId &&
          model.store.teamManager
            .getTeam(teamId)
            ?.hasPermission('RemoveJob') &&
          (!model.ownerTeamId || model.ownerTeamId !== teamId) && (
            <MenuItem
              isSelected={false}
              model={{ value: 'Remove from library', label: 'Remove from library' }}
              onClick={(_evt) => {
                model.dispatch('openRemoveJobFromTeamWindow', {
                  jobId: model.id,
                  teamId: teamId,
                });
              }} />
          )}

        {model.hasPermission('DeleteJob') &&
          (model.source.type === 'ExternalLibrary' ? (
            <MenuItem
              isSelected={false}
              model={{
                value: 'Remove from CLIPr',
                label: 'Remove from CLIPr',
              }}
              onClick={(_evt) => {
                model.dispatch('openRemoveJobFromCliprWindow', {
                  teamId: teamId,
                  jobId: model.id,
                });
              }} />
          ) : (
            <MenuItem
              isSelected={false}
              model={{ value: 'Delete', label: 'Delete' }}
              onClick={(_evt) => {
                model.dispatch('openDeleteJobWindow', {
                  jobId: model.id,
                  teamId: teamId,
                });
              }} />
          ))}
      </>
    );
  }
  return null;
});

export const CardOverlay = observer(({ model }: { model: JobModel }) => {
  let content: Maybe<ReactNode>;

  const className = classNames('card-overlay', {
    'done': model.isDone && model.isPublished && model.isMediaDone,
    'processing': model.isInProgress || model.isMediaProcessing,
    'analyzing': model.isProcessing,
    'failed': model.isFailed || model.isMediaFailed,
    'unpublish': !model.isPublished,
    'transient': model.isTransient
  });

  // TODO: convert to else-if chain after validating each condition is mutually exclusive

  const isDocumentJob = model.isDocumentJob;
  const draftName = model.isSubpoenaRequestDraft ? 'subpoena request' : 'warrant narrative';
  const inProgressCopy = model.isDocumentDraft
    ? <><b>CLIPr</b> is <b>drafting</b> your <b>{draftName}</b>.</>
    : model.isDocumentTranslation
      ? <><b>CLIPr</b> is <b>translating</b> your document.</>
      : <><b>CLIPr</b> is <b>ingesting</b> your video.</>;
  const failedCopy = model.isDocumentDraft
    ? `Failed to draft ${draftName}`
    : model.isDocumentTranslation
      ? 'Failed to translate document'
      : 'Failed to ingest video';

  if (model.isMediaProcessing || model.isInProgress) {
    content = (
      <>
        <IngestingSVGIcon />
        <p>
          {inProgressCopy}
          {!isDocumentJob && <><br />{BrowserIngestionEstimate}</>}
        </p>
        {!isDocumentJob && <IngestionProgress progressPercentage={model.progressPercentage} />}
      </>
    );
  }

  if (model.isMediaFailed) {
    content = (
      <>
        <TextInputErrorSVGIcon />
        <p>{failedCopy}</p>
      </>
    );
  }

  if (!model.isPublished) {
    content = (
      <>
        <TextInputWarningSVGIcon />
        <p>{isDocumentJob ? 'Document' : 'Video'} is no longer available</p>
        <p className="small">If you think this is an error, please <a href={'mailto:' + import.meta.env.VITE_EMAIL_QA}>Contact Us &#8594;</a></p>
      </>
    );
  }

  if (model.isLiveEnded) {
    content = (
      <>
        <TextInputWarningSVGIcon />
        <p>Live ended. Waiting for manual ingest</p>
      </>
    );
  }

  if (model.isTransient) {
    content = (
      <>
        <IngestingSVGIcon />
        <p>
          <b>CLIPr</b> is <b>preparing</b> your {model.isDocumentDraft ? draftName : model.isDocumentTranslation ? 'document translation' : 'video'}.
          {!isDocumentJob && <><br />{BrowserIngestionEstimate}</>}
        </p>
        {!isDocumentJob && <IngestionProgress progressPercentage={model.progressPercentage} />}
      </>
    );
  }

  return <div className={className}>{content}</div>;
});
